// Utilities for preserving the originally-requested URL across the login redirect flow.
// Used by PFM-2565 to send users back to the page they were trying to access.
//
// We use sessionStorage so the value survives the hard reload caused by `window.location.href = '/logout'`
// in App.js, but is automatically cleared when the user closes the tab.

const STORAGE_KEY = 'pfm.postLoginRedirect';

// Paths we should NOT save as a post-login redirect target (they're either auth/policy pages
// or the bare home page, which is the default landing already).
const EXCLUDED_PATTERNS = [/^\/?$/, /^\/logout$/, /^\/register(\/|$)/, /^\/policy(\/|$)/, /^\/home$/];

const isPathSavable = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return false;
  return !EXCLUDED_PATTERNS.some((re) => re.test(pathname));
};

export const savePostLoginRedirect = (location) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const loc = location || window.location;
    const pathname = loc.pathname || '';
    if (!isPathSavable(pathname)) return;
    const search = loc.search || '';
    const hash = loc.hash || '';
    window.sessionStorage.setItem(STORAGE_KEY, `${pathname}${search}${hash}`);
  } catch (e) {
    // sessionStorage may be unavailable (private mode, etc.); fail silent
  }
};

export const consumePostLoginRedirect = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const val = window.sessionStorage.getItem(STORAGE_KEY);
    if (val) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
    return val || null;
  } catch (e) {
    return null;
  }
};

export const peekPostLoginRedirect = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage.getItem(STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
};

export const clearPostLoginRedirect = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

// Smart-redirect mappings (PFM-2565):
//   /tasks/list/[processed_task_count_id]      -> /tasks/list
//   /tasks/detail/[task_id]                    -> /tasks/list
//   /reports/assessments/[assessment_id]       -> /reports/list
//   /reports/assessment/[assessment_id]        -> /reports/list (current route shape)
//   /library/[edition_id]/...                  -> /library
const SMART_REDIRECT_MAP = [
  { pattern: /^\/tasks\/list\/[^/]+/, redirect: '/tasks/list' },
  { pattern: /^\/tasks\/detail\/[^/]+/, redirect: '/tasks/list' },
  // PFM-2545: the static create-from-template link, if its target is gone or the
  // user lacks access, falls back to the assessments list (matched before the more
  // general /reports/assessment* patterns below).
  { pattern: /^\/reports\/create\/[^/]+/, redirect: '/reports/list' },
  { pattern: /^\/reports\/assessments?\/[^/]+/, redirect: '/reports/list' },
  { pattern: /^\/reports\/assessment-template\/[^/]+/, redirect: '/reports/list' },
  { pattern: /^\/library\/[^/]+/, redirect: '/library' },
];

export const getSmartRedirectFor = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return null;
  const match = SMART_REDIRECT_MAP.find((m) => m.pattern.test(pathname));
  return match ? match.redirect : null;
};

export const ACCESS_ALERT_QUERY = 'accessAlert';
export const ACCESS_ALERT_MESSAGE = "This page either doesn't exist or you do not have access.";

// PFM-2640: distinct alert value/message for when a shared link's temp-access
// token redeems fine but the underlying content is gone (404 on /sharehash/v1).
// A real generated link that no longer resolves means the content once existed
// and was deleted, so we tell the viewer exactly that.
export const SHARE_DELETED_ALERT_VALUE = 'shareDeleted';
export const SHARE_DELETED_ALERT_MESSAGE = 'This shared assessment has been deleted.';

// PFM-2545: the static create-from-template link has two failure cases, both of
// which bounce the user to the assessments list with a plain-text snackbar.
//   a. user lacks the "Create Assessment" permission
//   b. the template is gone (deleted) or the user has no access to it
export const NO_CREATE_PERMISSION_ALERT_VALUE = 'noCreatePermission';
export const NO_CREATE_PERMISSION_ALERT_MESSAGE = 'You do not have permission to create assessments.';

export const TEMPLATE_NOT_FOUND_ALERT_VALUE = 'templateNotFound';
export const TEMPLATE_NOT_FOUND_ALERT_MESSAGE = 'Template not found, click "Create" to start an Assessment.';
